import { useCallback, useEffect, useRef, useState } from 'react';

import client from '@api';
import { markBackgroundTasksSeenMutation } from '@graphql/mutations/backgroundTasks';
import { backgroundTasksTrayQuery } from '@graphql/queries/backgroundTasks';
import { backgroundTaskUpdatedSubscription } from '@graphql/subscription/backgroundTasks';
import { useWsReconnect } from '@hooks/useWsReconnect';
import { ErrorCategory, handleError } from '@services/errorService';

import type { GqlBackgroundTaskOutput } from '@app-types/index';

interface BackgroundTaskUpdatedData {
  backgroundTaskUpdated?: GqlBackgroundTaskOutput;
}

interface BackgroundTasksTrayResponse {
  backgroundTasksTray?: {
    items: GqlBackgroundTaskOutput[];
  };
}

interface UseBackgroundTasksResult {
  tasks: GqlBackgroundTaskOutput[];
  dismissTask: (id: string) => void;
  refetch: () => Promise<void>;
}

const TRAY_LIMIT = 10;
const TERMINAL_LINGER_MS = 5_000;

const isTerminal = (status: string): boolean =>
  status === 'DONE' || status === 'PARTIAL' || status === 'FAILED';

const toSortedArray = (
  map: Map<string, GqlBackgroundTaskOutput>,
): GqlBackgroundTaskOutput[] =>
  Array.from(map.values()).sort((a, b) =>
    b.updatedAt.localeCompare(a.updatedAt),
  );

/**
 * Subscription-driven tray state. Lifecycle:
 *
 * 1. Open `backgroundTaskUpdated` subscription FIRST. Frames arriving
 *    while the initial snapshot is in flight are buffered into a
 *    lookaside `Map`.
 * 2. Run the `backgroundTasksTray` snapshot query.
 * 3. On resolve, seed the working `Map` with snapshot rows, then drain
 *    the lookaside on top — upsert by `task.id`, NO `updatedAt`
 *    comparison (title-backfill updates do not bump `updatedAt`).
 * 4. From this point each subscription frame upserts directly.
 * 5. On WS reconnect (ADR-009), re-run the snapshot.
 */
export const useBackgroundTasks = (): UseBackgroundTasksResult => {
  const [tasks, setTasks] = useState<GqlBackgroundTaskOutput[]>([]);
  const workingRef = useRef(new Map<string, GqlBackgroundTaskOutput>());
  const lookasideRef = useRef(new Map<string, GqlBackgroundTaskOutput>());
  const snapshotInFlightRef = useRef(false);
  const acknowledgedRef = useRef(new Set<string>());
  const hiddenRef = useRef(new Set<string>());
  const removalTimersRef = useRef(
    new Map<string, ReturnType<typeof setTimeout>>(),
  );

  const publish = useCallback((): void => {
    setTasks(toSortedArray(workingRef.current));
  }, []);

  const acknowledge = useCallback((ids: string[]): void => {
    if (ids.length === 0) return;
    void client()
      .mutate({
        mutation: markBackgroundTasksSeenMutation,
        variables: { ids },
      })
      .catch((err: Error) =>
        handleError(err, { category: ErrorCategory.GRAPHQL, silent: true }),
      );
  }, []);

  const ingestTask = useCallback(
    (
      target: Map<string, GqlBackgroundTaskOutput>,
      task: GqlBackgroundTaskOutput,
    ): string | null => {
      if (!isTerminal(task.status)) {
        hiddenRef.current.delete(task.id);
        acknowledgedRef.current.delete(task.id);
        const timer = removalTimersRef.current.get(task.id);
        if (timer) {
          clearTimeout(timer);
          removalTimersRef.current.delete(task.id);
        }
        target.set(task.id, task);
        return null;
      }

      if (hiddenRef.current.has(task.id)) return null;
      target.set(task.id, task);

      if (!removalTimersRef.current.has(task.id)) {
        const timer = setTimeout(() => {
          removalTimersRef.current.delete(task.id);
          hiddenRef.current.add(task.id);
          workingRef.current.delete(task.id);
          publish();
        }, TERMINAL_LINGER_MS);
        removalTimersRef.current.set(task.id, timer);
      }

      if (acknowledgedRef.current.has(task.id)) return null;
      acknowledgedRef.current.add(task.id);
      return task.id;
    },
    [publish],
  );

  const refetch = useCallback(async (): Promise<void> => {
    snapshotInFlightRef.current = true;
    try {
      const { data } = await client().query<BackgroundTasksTrayResponse>({
        query: backgroundTasksTrayQuery,
        variables: { input: { limit: TRAY_LIMIT } },
        fetchPolicy: 'no-cache',
      });
      const items = data?.backgroundTasksTray?.items ?? [];

      const next = new Map<string, GqlBackgroundTaskOutput>();
      const idsToAcknowledge: string[] = [];
      items.forEach((item) => {
        const id = ingestTask(next, item);
        if (id) idsToAcknowledge.push(id);
      });
      // Drain lookaside on top — upsert by id, no updatedAt comparison.
      lookasideRef.current.forEach((item) => {
        const id = ingestTask(next, item);
        if (id) idsToAcknowledge.push(id);
      });
      lookasideRef.current.clear();

      workingRef.current = next;
      publish();
      acknowledge(idsToAcknowledge);
    } catch (err) {
      handleError(err as Error, {
        category: ErrorCategory.GRAPHQL,
        silent: true,
      });
    } finally {
      snapshotInFlightRef.current = false;
    }
  }, [acknowledge, ingestTask, publish]);

  const dismissTask = useCallback(
    (id: string): void => {
      const task = workingRef.current.get(id);
      if (task && isTerminal(task.status)) {
        hiddenRef.current.add(id);
        if (!acknowledgedRef.current.has(id)) {
          acknowledgedRef.current.add(id);
          acknowledge([id]);
        }
      }
      const timer = removalTimersRef.current.get(id);
      if (timer) {
        clearTimeout(timer);
        removalTimersRef.current.delete(id);
      }
      workingRef.current.delete(id);
      publish();
    },
    [acknowledge, publish],
  );

  useEffect(() => {
    // Capture ref Map identity so the cleanup uses the same instance the
    // effect set up. `useRef.current` never reassigns here — same Map for
    // the lifetime of the component — but the eslint rule wants this
    // explicit capture.
    const lookaside = lookasideRef.current;
    const removalTimers = removalTimersRef.current;

    const observable = client().subscribe({
      query: backgroundTaskUpdatedSubscription,
    });
    const sub = observable.subscribe({
      next: ({ data }: { data: BackgroundTaskUpdatedData }) => {
        const task = data?.backgroundTaskUpdated;
        if (!task) return;
        if (snapshotInFlightRef.current) {
          lookaside.set(task.id, task);
          return;
        }
        const id = ingestTask(workingRef.current, task);
        publish();
        if (id) acknowledge([id]);
      },
      error: (err: Error) =>
        handleError(err, { category: ErrorCategory.GRAPHQL, silent: true }),
    });

    refetch();

    return () => {
      sub.unsubscribe();
      lookaside.clear();
      removalTimers.forEach(clearTimeout);
      removalTimers.clear();
    };
  }, [acknowledge, ingestTask, publish, refetch]);

  // ADR-009: re-fetch the snapshot on every WS reconnect.
  useWsReconnect(refetch);

  return { tasks, dismissTask, refetch };
};

export default useBackgroundTasks;
